import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Modal,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { keyframes } from "@mui/system";

const API_BASE =
  window.location.hostname.includes("cityfarmgame.com")
    ? "https://bigdogbonegame.com/api/bdb/pet"
    : "/api/bdb/pet";
const CITYFARM_URL = "https://www.cityfarmgame.com/";

/* ============================================================
   IMAGES
============================================================ */

const dogImages = {
  idle: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870756/dog_walk_1.png_xhd04p.png",
  walk1: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870756/dog_walk_1.png_xhd04p.png",
  walk2: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870756/dog_walk_1.png_xhd04p.png",
  walk3: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870756/dog_walk_1.png_xhd04p.png",
  eat: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870749/dog_eat.png_t8b4uq.png",
  drink: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870751/dog_drink_d8wpqw.png",
  play: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870752/dog_play.png_kunios.png",
  sleep: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870750/dog_sleep.png_ifeofv.png",
  weak: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870746/dog_fallen.png_dsegnx.png",
  fallen: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870746/dog_fallen.png_dsegnx.png",
};

const ACTION_VIEW_TIME = {
  eat: 6500,
  drink: 6500,
  play: 5500,
  walk: 6500,
  sleep: 6000,
};

const benefitLevels = [
  {
    level: "1-9",
    benefit: { en: "+3% Mini-Hueso bonus", es: "+3% de bono de Mini-Hueso" },
    daily: { en: "Base daily reward", es: "Recompensa diaria base" },
  },
  {
    level: "10-14",
    benefit: { en: "+2% Mini-Hueso bonus", es: "+2% de bono de Mini-Hueso" },
    daily: { en: "Higher daily reward", es: "Recompensa diaria superior" },
  },
  {
    level: "15-19",
    benefit: {
      en: "+2% Mini-Hueso + Energy bonus",
      es: "+2% de Mini-Hueso + bono de Energía",
    },
    daily: { en: "Higher daily reward", es: "Recompensa diaria superior" },
  },
  {
    level: "20-29",
    benefit: {
      en: "+3% Mini-Hueso + Walk reduction",
      es: "+3% de Mini-Hueso + reducción de caminata",
    },
    daily: { en: "Improved reward", es: "Recompensa mejorada" },
  },
  {
    level: "30-49",
    benefit: { en: "Adult pet bonuses", es: "Bonos de mascota adulta" },
    daily: { en: "Strong reward", es: "Recompensa avanzada" },
  },
  {
    level: "50+",
    benefit: { en: "Legendary pet bonuses", es: "Bonos de mascota legendaria" },
    daily: { en: "Best reward tier", es: "Mejor nivel de recompensa" },
  },
];

/* ============================================================
   ANIMATIONS
============================================================ */

const urgentPulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 rgba(255, 23, 68, 0); }
  50% { transform: scale(1.025); box-shadow: 0 0 22px rgba(255, 23, 68, 0.85); }
  100% { transform: scale(1); box-shadow: 0 0 0 rgba(255, 23, 68, 0); }
`;

const warningPulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 rgba(255, 179, 0, 0); }
  50% { transform: scale(1.015); box-shadow: 0 0 18px rgba(255, 179, 0, 0.7); }
  100% { transform: scale(1); box-shadow: 0 0 0 rgba(255, 179, 0, 0); }
`;

const readyPulse = keyframes`
  0% { box-shadow: 0 0 0 rgba(156, 39, 176, 0); }
  50% { box-shadow: 0 0 18px rgba(156, 39, 176, 0.75); }
  100% { box-shadow: 0 0 0 rgba(156, 39, 176, 0); }
`;

/* ============================================================
   HELPERS
============================================================ */

function getPetImage(pet, animation, walkFrame = 1) {
  if (animation === "eat") return dogImages.eat;
  if (animation === "drink") return dogImages.drink;
  if (animation === "play") return dogImages.play;
  if (animation === "sleep") return dogImages.sleep;

  if (pet?.status === "fallen") return dogImages.fallen;
  if (pet?.status === "weak") return dogImages.weak;

  if (animation === "walk") {
    if (walkFrame === 1) return dogImages.walk1;
    if (walkFrame === 2) return dogImages.walk2;
    return dogImages.walk3;
  }

  return dogImages.idle;
}

function preloadImages() {
  Object.values(dogImages).forEach((src) => {
    const img = new Image();
    img.src = src;
  });
}

function getCookieToken(name = "token") {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

function getToken() {
  return (
    sessionStorage.getItem("token") ||
    localStorage.getItem("token") ||
    getCookieToken("token")
  );
}

function petApi() {
  return axios.create({
    baseURL: API_BASE,
    headers: { token: getToken() },
  });
}

function fmtNumber(value) {
  return Number(value || 0).toLocaleString(undefined, {
    maximumFractionDigits: 3,
  });
}

function fmtDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function clampCaretakerDays(value) {
  const parsed = parseInt(value, 10);
  if (!Number.isFinite(parsed)) return 1;
  return Math.max(1, Math.min(20, parsed));
}

function caretakerReasonText(reason, language = "en") {
  const code = String(reason || "").trim().toUpperCase();
  const maps = {
    en: {
      NOT_ENOUGH_FOOD: "Not enough Croquettes in CityFarm.",
      NOT_ENOUGH_WATER: "Not enough Water in CityFarm.",
      CITY_USER_NOT_FOUND: "The CityFarm user was not found.",
      PET_FALLEN: "The puppy is fallen. Revive it first.",
      NO_DAYS_REMAINING: "No hired days remain.",
      COMPLETED: "The service ended after completing the purchased days.",
      USER_CANCELLED: "Manually cancelled by the user.",
      MISSING_RESOURCES: "Resources are missing in CityFarm.",
      DISABLED: "Caretaker disabled.",
    },
    es: {
      NOT_ENOUGH_FOOD: "Faltan Croquetas en CityFarm.",
      NOT_ENOUGH_WATER: "Falta Agua en CityFarm.",
      CITY_USER_NOT_FOUND: "No se encontró el usuario en CityFarm.",
      PET_FALLEN: "El perro está caído. Debe revivirse primero.",
      NO_DAYS_REMAINING: "No quedan días contratados.",
      COMPLETED: "El servicio terminó al completar los días comprados.",
      USER_CANCELLED: "Cancelado manualmente por el usuario.",
      MISSING_RESOURCES: "Faltan recursos en CityFarm.",
      DISABLED: "Cuidador desactivado.",
    },
  };
  const map = maps[language] || maps.en;
  return map[code] || (code ? `${language === "es" ? "Motivo" : "Reason"}: ${code}` : "—");
}

function compactHistory(history = []) {
  if (!Array.isArray(history)) return [];
  return history.filter(Boolean).slice(0, 6);
}

function statusColor(status) {
  if (status === "alive") return "#00e676";
  if (status === "weak") return "#ffb300";
  if (status === "fallen") return "#ff1744";
  return "#ffb300";
}

function statusText(status, language = "en") {
  const labels = {
    alive: { en: "Alive", es: "Vivo" },
    weak: { en: "Weak", es: "Débil" },
    fallen: { en: "Fallen", es: "Caído" },
  };
  return labels[status]?.[language] || status || (language === "es" ? "Desconocido" : "Unknown");
}

function stageText(stage, language = "en") {
  const labels = {
    puppy: { en: "Puppy", es: "Cachorro" },
    young: { en: "Young", es: "Joven" },
    adult: { en: "Adult", es: "Adulto" },
    veteran: { en: "Veteran", es: "Veterano" },
    legendary: { en: "Legendary", es: "Legendario" },
  };
  return labels[stage]?.[language] || stage || (language === "es" ? "Cachorro" : "Puppy");
}

function getBarColor(value) {
  const safeValue = Number(value || 0);
  if (safeValue > 65) return "#00e676";
  if (safeValue > 35) return "#ffb300";
  return "#ff1744";
}

function getUrgency(value, mode = "lowIsBad") {
  const safeValue = Number(value || 0);
  const score = mode === "highIsBad" ? 100 - safeValue : safeValue;

  if (score <= 25) return "critical";
  if (score <= 45) return "warning";
  return "normal";
}

function isCooldownReady(cooldown) {
  if (!cooldown) return true;

  const seconds = Number(cooldown?.seconds ?? cooldown?.remainingSeconds ?? cooldown?.remaining ?? 0);
  const text = String(cooldown?.text || "").toLowerCase();

  return seconds <= 0 && (!text || text === "ready" || text.includes("ready"));
}

function getActionHint(pet, cooldowns, language = "en") {
  const tr = (en, es) => (language === "es" ? es : en);

  if (!pet) return tr("Loading puppy status...", "Cargando el estado del cachorro...");
  if (pet.status === "fallen") {
    return tr(
      "Your puppy is fallen. Revive it first.",
      "Tu cachorro está caído. Debes revivirlo primero."
    );
  }

  const needs = [
    { label: tr("Feed", "Alimentar"), value: Number(pet.hunger || 0) },
    { label: tr("Give Water", "Dar Agua"), value: Number(pet.thirst || 0) },
    { label: tr("Play", "Jugar"), value: Number(pet.happiness || 0) },
    { label: tr("Health", "Salud"), value: Number(pet.health || 0) },
  ].sort((a, b) => a.value - b.value);

  const first = needs[0];

  if (first.value <= 25) {
    return tr(
      `Critical: use ${first.label} first.`,
      `Crítico: primero debes usar ${first.label}.`
    );
  }
  if (first.value <= 45) {
    return tr(
      `Attention: ${first.label} should be next.`,
      `Atención: la siguiente acción debe ser ${first.label}.`
    );
  }
  if (isCooldownReady(cooldowns?.claim)) {
    return tr(
      "Daily reward looks ready. Claim it now.",
      "La recompensa diaria parece estar lista. Reclámala ahora."
    );
  }
  return tr(
    "Your puppy is stable. Keep the routine active.",
    "Tu cachorro está estable. Mantén activa la rutina."
  );
}

function localizeBenefitLevel(row, language = "en") {
  return {
    ...row,
    benefit: row?.benefit?.[language] || row?.benefit?.en || "",
    daily: row?.daily?.[language] || row?.daily?.en || "",
  };
}

function getCurrentBenefitByLevel(level, language = "en") {
  const lvl = Number(level || 0);
  let row = benefitLevels[0];

  if (lvl >= 50) row = benefitLevels[5];
  else if (lvl >= 30) row = benefitLevels[4];
  else if (lvl >= 20) row = benefitLevels[3];
  else if (lvl >= 15) row = benefitLevels[2];
  else if (lvl >= 10) row = benefitLevels[1];

  return localizeBenefitLevel(row, language);
}

function StatBar({ label, value, icon }) {
  const safeValue = Math.max(0, Math.min(100, Number(value || 0)));
  const urgency = getUrgency(safeValue);

  return (
    <Box
      sx={{
        width: "100%",
        mb: 0.8,
        px: urgency === "critical" ? 0.6 : 0,
        py: urgency === "critical" ? 0.35 : 0,
        borderRadius: 1.5,
        background: urgency === "critical" ? "rgba(255,23,68,0.08)" : "transparent",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
        <Typography sx={{ color: "white", fontSize: 12, fontWeight: urgency !== "normal" ? 900 : 600 }}>
          {icon} {label}
        </Typography>
        <Typography sx={{ color: "#ffcc80", fontSize: 12, fontWeight: 900 }}>
          {Math.round(safeValue)}%
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={safeValue}
        sx={{
          height: 8,
          borderRadius: 10,
          backgroundColor: "rgba(255,255,255,0.16)",
          "& .MuiLinearProgress-bar": {
            borderRadius: 10,
            backgroundColor: getBarColor(safeValue),
          },
        }}
      />
    </Box>
  );
}

function InfoCard({ title, children, sx = {} }) {
  return (
    <Box
      sx={{
        background: "linear-gradient(180deg, rgba(255,255,255,0.09), rgba(255,255,255,0.035))",
        border: "1px solid rgba(255,152,0,0.28)",
        borderRadius: 2.5,
        p: 1.15,
        boxShadow: "inset 0 0 18px rgba(255,152,0,0.04)",
        ...sx,
      }}
    >
      <Typography sx={{ color: "#ffb300", mb: 0.8, fontWeight: 900, fontSize: 13.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function SmallRow({ label, value, accent = false }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.38 }}>
      <Typography sx={{ color: "rgba(255,255,255,0.74)", fontSize: 12 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          color: accent ? "#ffcc80" : "white",
          fontSize: 12,
          fontWeight: 900,
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function ActionButton({ children, urgency = "normal", readyPulseOn = false, sx = {}, ...props }) {
  const pulseAnimation =
    urgency === "critical"
      ? `${urgentPulse} 1s ease-in-out infinite`
      : urgency === "warning"
        ? `${warningPulse} 1.5s ease-in-out infinite`
        : readyPulseOn
          ? `${readyPulse} 1.6s ease-in-out infinite`
          : "none";

  return (
    <Button
      variant="contained"
      {...props}
      sx={{
        minHeight: 46,
        borderRadius: 2,
        fontWeight: 950,
        letterSpacing: 0.25,
        animation: pulseAnimation,
        border: urgency === "critical" ? "2px solid rgba(255,255,255,0.75)" : "1px solid rgba(255,255,255,0.12)",
        textShadow: "0 1px 2px rgba(0,0,0,0.35)",
        transition: "transform .15s ease, filter .15s ease",
        "&:hover": {
          transform: "translateY(-1px)",
          filter: "brightness(1.08)",
        },
        ...sx,
      }}
    >
      {children}
    </Button>
  );
}

/* ============================================================
   MAIN COMPONENT
============================================================ */

export default function PetOverlay() {
  const [petData, setPetData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openPanel, setOpenPanel] = useState(false);
  const [nickname, setNickname] = useState("");
  const [message, setMessage] = useState(null);
  const [animation, setAnimation] = useState("idle");
  const [walkFrame, setWalkFrame] = useState(1);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showCaretakerHelp, setShowCaretakerHelp] = useState(false);
  const [caretakerDays, setCaretakerDays] = useState(1);
  const [caretakerStatus, setCaretakerStatus] = useState(null);
  const [caretakerHelp, setCaretakerHelp] = useState(null);
  const [language, setLanguage] = useState(() => {
    try {
      return window.localStorage?.getItem("bdb_language") === "es" ? "es" : "en";
    } catch {
      return "en";
    }
  });

  const tr = (en, es) => (language === "es" ? es : en);

  const changeLanguage = (nextLanguage) => {
    const next = nextLanguage === "es" ? "es" : "en";
    setLanguage(next);
    try {
      window.localStorage?.setItem("bdb_language", next);
      window.dispatchEvent(
        new CustomEvent("bdb-language-change", { detail: { language: next } })
      );
    } catch {
      // The selected language still works for the current session.
    }
  };

  const animationTimerRef = useRef(null);

  const pet = petData?.pet;
  const cityFarm = petData?.cityFarm;
  const bdb = petData?.bdb;
  const benefit = petData?.benefit;
  const cooldowns = petData?.cooldowns;
  // Estado REAL del cuidador.
  // Soporta ambos formatos para no romper si el backend responde con:
  // 1) pet.caretaker.active / pet.caretaker.daysRemaining
  // 2) pet.caretakerActive / pet.caretakerDaysRemaining
  // 3) caretaker.active / caretaker.daysRemaining
  const rawPet = caretakerStatus?.pet || petData?.pet || {};
  const caretaker = rawPet?.caretaker || petData?.pet?.caretaker || {};
  const caretakerPanel = caretakerStatus?.caretaker || petData?.caretaker || {};
  const caretakerConfig =
    caretakerStatus?.caretaker?.config ||
    petData?.caretaker?.config ||
    caretakerStatus?.config ||
    caretakerHelp?.config ||
    caretakerHelp ||
    {};
  const caretakerActive = Boolean(
    caretaker?.active ??
      rawPet?.caretakerActive ??
      caretaker?.caretakerActive ??
      caretaker?.isActive ??
      caretakerPanel?.active ??
      caretakerPanel?.caretakerActive ??
      false
  );
  const caretakerDaysRemaining = Number(
    caretaker?.daysRemaining ??
      rawPet?.caretakerDaysRemaining ??
      caretaker?.caretakerDaysRemaining ??
      caretaker?.remainingDays ??
      caretakerPanel?.daysRemaining ??
      caretakerPanel?.caretakerDaysRemaining ??
      0
  );
  const caretakerNextRunAt =
    caretaker?.nextRunAt ||
    rawPet?.caretakerNextRunAt ||
    caretaker?.caretakerNextRunAt ||
    caretakerPanel?.nextRunAt ||
    caretakerPanel?.caretakerNextRunAt ||
    null;
  const caretakerPricePerDay = Number(
    caretakerConfig?.pricePerDay ??
      caretakerConfig?.huesoPerDay ??
      caretakerStatus?.pricePerDay ??
      500
  );
  const caretakerMaxDays = Number(
    caretakerConfig?.maxDays ??
      caretakerStatus?.maxDays ??
      20
  );
  const caretakerTotal = clampCaretakerDays(caretakerDays) * caretakerPricePerDay;
  const caretakerRunEveryHours = Number(
    caretakerConfig?.runEveryHours ||
      Math.round(Number(caretakerConfig?.runIntervalMs || 5 * 60 * 60 * 1000) / 36e5) ||
      5
  );
  const caretakerDisabledReason =
    caretaker?.disabledReason ||
    rawPet?.caretakerDisabledReason ||
    caretaker?.caretakerDisabledReason ||
    caretakerPanel?.disabledReason ||
    "";
  const caretakerDisabledText =
    caretaker?.disabledReasonText ||
    caretakerPanel?.disabledReasonText ||
    caretakerReasonText(caretakerDisabledReason, language);
  const caretakerLastRunAt =
    caretaker?.lastRunAt ||
    rawPet?.caretakerLastRunAt ||
    caretaker?.caretakerLastRunAt ||
    caretakerPanel?.lastRunAt ||
    null;
  const caretakerLastRunStatus =
    caretaker?.lastRunStatus ||
    rawPet?.caretakerLastRunStatus ||
    caretakerPanel?.lastRunStatus ||
    "";
  const caretakerLastRunMessage =
    caretaker?.lastRunMessage ||
    rawPet?.caretakerLastRunMessage ||
    caretakerPanel?.lastRunMessage ||
    "";
  const caretakerTotalRuns = Number(
    caretaker?.totalRuns ?? rawPet?.caretakerTotalRuns ?? caretakerPanel?.totalRuns ?? 0
  );
  const caretakerHistory = compactHistory(
    caretaker?.runHistory || rawPet?.caretakerRunHistory || caretakerPanel?.runHistory || []
  );

  const displayName = useMemo(() => {
    if (!pet) return language === "es" ? "Mi Cachorro" : "My Puppy";
    return pet.nickname || pet.birthName || (language === "es" ? "Mi Cachorro" : "My Puppy");
  }, [pet, language]);

  const waterQty = cityFarm?.inventory?.Water ?? 0;
  const foodQty = cityFarm?.inventory?.Croquettes ?? 0;
  const claimAmount = benefit?.dailyClaim?.miniHueso || 0;
  const canUseActions = pet?.status !== "fallen";
  const currentLevelBenefit = getCurrentBenefitByLevel(pet?.level || 1, language);

  const hungerUrgency = getUrgency(pet?.hunger || 0);
  const thirstUrgency = getUrgency(pet?.thirst || 0);
  const happinessUrgency = getUrgency(pet?.happiness || 0);
  const claimReady = isCooldownReady(cooldowns?.claim);
  const priorityHint = getActionHint(pet, cooldowns, language);

  const notifyGlobalBalanceRefresh = (data) => {
    window.dispatchEvent(
      new CustomEvent("actualizar-saldos", {
        detail: {
          bdb: data?.bdb || null,
          pet: data?.pet || null,
          source: "PetOverlay",
        },
      })
    );
  };

  const clearAnimationTimer = () => {
    if (animationTimerRef.current) {
      clearTimeout(animationTimerRef.current);
      animationTimerRef.current = null;
    }
  };

  const setTimedAnimation = (nextAnimation, duration) => {
    clearAnimationTimer();
    setAnimation(nextAnimation);
    animationTimerRef.current = setTimeout(() => {
      setAnimation("idle");
    }, duration || 3000);
  };

  const refreshPet = async ({ silent = false, keepMessage = true, syncGlobal = true } = {}) => {
    try {
      if (!silent) setLoading(true);

      const res = await petApi().get("/me");

      setPetData(res.data);
      setNickname(res.data?.pet?.nickname || "");
      setLastUpdated(new Date());

      if (res.data?.caretaker) {
        setCaretakerStatus((prev) => ({ ...(prev || {}), caretaker: res.data.caretaker }));
      }

      if (syncGlobal) {
        notifyGlobalBalanceRefresh(res.data);
      }

      if (!keepMessage) setMessage(null);

      return res.data;
    } catch (err) {
      console.error("[PET FRONT] load error:", err);

      if (!silent) {
        setMessage({
          type: "error",
          text:
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            tr("Could not load your puppy.", "No se pudo cargar tu cachorro."),
        });
      }

      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const updateFromResponseThenRefresh = async (resData) => {
    setPetData((prev) => ({
      ...prev,
      ...resData,
      pet: resData?.pet || prev?.pet,
      benefit: resData?.benefit || prev?.benefit,
      cooldowns: resData?.cooldowns || prev?.cooldowns,
      cityFarm: {
        ...(prev?.cityFarm || {}),
        ...(resData?.cityFarm || {}),
      },
      bdb: {
        ...(prev?.bdb || {}),
        ...(resData?.bdb || {}),
      },
    }));

    if (resData?.caretaker) {
      setCaretakerStatus((prev) => ({ ...(prev || {}), caretaker: resData.caretaker }));
    }

    notifyGlobalBalanceRefresh(resData);

    await refreshPet({
      silent: true,
      keepMessage: true,
      syncGlobal: true,
    });

    await refreshCaretakerStatus({ silent: true });
  };

  const refreshCaretakerStatus = async ({ silent = true } = {}) => {
    try {
      const res = await petApi().get("/caretaker/status");
      setCaretakerStatus(res.data || null);
      return res.data;
    } catch (err) {
      if (!silent) {
        setMessage({
          type: "error",
          text:
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            tr("Could not load caretaker status.", "No se pudo cargar el estado del cuidador."),
        });
      }
      return null;
    }
  };

  const loadCaretakerHelp = async () => {
    try {
      const res = await petApi().get("/caretaker/help");
      setCaretakerHelp(res.data || null);
      return res.data;
    } catch (err) {
      setCaretakerHelp({
        pricePerDay: 100,
        maxDays: 20,
        waterPerDay: 2,
        croquettesPerDay: 2,
        description:
          "The Dog Caretaker feeds, gives water, plays and walks your puppy every 5 hours while active.",
      });
      return null;
    }
  };

  const openCaretakerHelpPanel = async () => {
    setShowInstructions(false);
    setShowCaretakerHelp(true);
    await loadCaretakerHelp();
    await refreshCaretakerStatus({ silent: true });
  };

  const buyCaretaker = async () => {
    try {
      const days = clampCaretakerDays(caretakerDays);
      setLoading(true);
      setMessage(null);

      const res = await petApi().post("/caretaker/buy", { days });

      setMessage({
        type: "success",
        text:
          res.data?.message ||
          language === "es"
            ? `Cuidador del Perro contratado por ${days} día${days === 1 ? "" : "s"}.`
            : `Dog Caretaker hired for ${days} day${days === 1 ? "" : "s"}.`,
      });

      // Actualización inmediata del panel: evita que se vea Inactive/0 justo después de comprar.
      // Luego refrescamos contra el backend para confirmar el estado real.
      const optimisticCaretaker = res.data?.pet?.caretaker || res.data?.caretaker || {
        active: true,
        daysRemaining: days,
        daysPurchased: days,
      };

      setPetData((prev) => ({
        ...(prev || {}),
        ...res.data,
        pet: {
          ...((prev || {}).pet || {}),
          ...(res.data?.pet || {}),
          caretaker: {
            ...(((prev || {}).pet || {}).caretaker || {}),
            ...optimisticCaretaker,
            active: optimisticCaretaker?.active ?? true,
            daysRemaining: optimisticCaretaker?.daysRemaining ?? days,
          },
        },
      }));

      setCaretakerStatus((prev) => ({
        ...(prev || {}),
        pet: {
          ...((prev || {}).pet || {}),
          caretaker: {
            ...(((prev || {}).pet || {}).caretaker || {}),
            ...optimisticCaretaker,
            active: optimisticCaretaker?.active ?? true,
            daysRemaining: optimisticCaretaker?.daysRemaining ?? days,
          },
        },
        caretaker: {
          ...((prev || {}).caretaker || {}),
          ...(res.data?.caretaker || {}),
          active: optimisticCaretaker?.active ?? true,
          daysRemaining: optimisticCaretaker?.daysRemaining ?? days,
        },
      }));

      notifyGlobalBalanceRefresh(res.data);
      await refreshPet({ silent: true, keepMessage: true, syncGlobal: true });
      await refreshCaretakerStatus({ silent: true });
    } catch (err) {
      setMessage({
        type: "error",
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          tr("Could not activate the Dog Caretaker.", "No se pudo activar el Cuidador del Perro."),
      });
      await refreshCaretakerStatus({ silent: true });
    } finally {
      setLoading(false);
    }
  };

  const cancelCaretaker = async () => {
    try {
      setLoading(true);
      setMessage(null);

      const res = await petApi().post("/caretaker/cancel");

      setMessage({
        type: "success",
        text: res.data?.message || tr("Dog Caretaker cancelled.", "Cuidador del Perro cancelado."),
      });

      await updateFromResponseThenRefresh(res.data);
      await refreshCaretakerStatus({ silent: true });
    } catch (err) {
      setMessage({
        type: "error",
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          tr("Could not cancel the Dog Caretaker.", "No se pudo cancelar el Cuidador del Perro."),
      });
    } finally {
      setLoading(false);
    }
  };

  const runAction = async (action, actionAnimation) => {
    try {
      setLoading(true);
      setMessage(null);

      setTimedAnimation(actionAnimation, ACTION_VIEW_TIME[actionAnimation] || 4200);

      await new Promise((resolve) => setTimeout(resolve, 140));

      const res = await petApi().post(`/${action}`);

      setMessage({
        type: "success",
        text: res.data?.message || tr("Action completed successfully.", "Acción completada correctamente."),
      });

      await updateFromResponseThenRefresh(res.data);
    } catch (err) {
      console.error("[PET FRONT] action error:", err);

      const data = err?.response?.data;

      setMessage({
        type: "error",
        text: data?.cooldown?.text
          ? `${data?.message || tr("This action is on cooldown.", "Esta acción está en tiempo de espera.")} ${tr("Wait", "Espera")} ${data.cooldown.text}`
          : data?.message || data?.error || tr("Action failed.", "La acción falló."),
      });

      await refreshPet({
        silent: true,
        keepMessage: true,
        syncGlobal: true,
      });

      notifyGlobalBalanceRefresh();
      clearAnimationTimer();
      setAnimation("idle");
    } finally {
      setLoading(false);
    }
  };

  const feedPet = () => runAction("feed", "eat");
  const waterPet = () => runAction("water", "drink");
  const playPet = () => runAction("play", "play");
  const walkPet = () => runAction("walk", "walk");
  const claimDaily = () => runAction("claim-daily", "play");

  const saveNickname = async () => {
    try {
      const cleanNickname = String(nickname || "").trim();

      if (!cleanNickname || cleanNickname.length < 2) {
        setMessage({ type: "error", text: tr("Nickname must have at least 2 characters.", "El apodo debe tener al menos 2 caracteres.") });
        return;
      }

      setLoading(true);
      setMessage(null);

      const res = await petApi().post("/nickname", { nickname: cleanNickname });

      setMessage({ type: "success", text: res.data?.message || tr("Nickname updated.", "Apodo actualizado.") });

      await updateFromResponseThenRefresh(res.data);
    } catch (err) {
      setMessage({
        type: "error",
        text: err?.response?.data?.message || err?.response?.data?.error || tr("Could not update nickname.", "No se pudo actualizar el apodo."),
      });
    } finally {
      setLoading(false);
    }
  };

  const revivePet = async (paymentType) => {
    try {
      setLoading(true);
      setMessage(null);

      setTimedAnimation("play", 3000);

      const res = await petApi().post("/revive", { paymentType });

      setMessage({ type: "success", text: res.data?.message || tr("Puppy revived successfully.", "Cachorro revivido correctamente.") });

      await updateFromResponseThenRefresh(res.data);
    } catch (err) {
      setMessage({
        type: "error",
        text: err?.response?.data?.message || err?.response?.data?.error || tr("Could not revive your puppy.", "No se pudo revivir a tu cachorro."),
      });

      await refreshPet({ silent: true, keepMessage: true, syncGlobal: true });
      notifyGlobalBalanceRefresh();
      clearAnimationTimer();
      setAnimation("idle");
    } finally {
      setLoading(false);
    }
  };

  const openCityFarm = () => {
    window.open(CITYFARM_URL, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    const syncLanguage = (event) => {
      const eventLanguage = event?.detail?.language;
      const storedLanguage = window.localStorage?.getItem("bdb_language");
      const next = eventLanguage || storedLanguage;
      setLanguage(next === "es" ? "es" : "en");
    };

    window.addEventListener("storage", syncLanguage);
    window.addEventListener("bdb-language-change", syncLanguage);

    return () => {
      window.removeEventListener("storage", syncLanguage);
      window.removeEventListener("bdb-language-change", syncLanguage);
    };
  }, []);

  useEffect(() => {
    preloadImages();
    refreshPet();
    refreshCaretakerStatus({ silent: true });
    loadCaretakerHelp();

    const interval = setInterval(() => {
      refreshPet({ silent: true, keepMessage: true, syncGlobal: true });
      refreshCaretakerStatus({ silent: true });
    }, 60000);

    return () => {
      clearInterval(interval);
      clearAnimationTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const frameInterval = setInterval(() => {
      setWalkFrame((prev) => (prev >= 3 ? 1 : prev + 1));
    }, 450);

    return () => clearInterval(frameInterval);
  }, []);

  if (!pet) return null;

  const floatingImage = getPetImage(pet, animation === "idle" ? "walk" : animation, walkFrame);
  const panelImage = getPetImage(pet, animation, walkFrame);

  return (
    <>
      {/* ======================================================
          FLOATING SPHERE DOG
      ====================================================== */}
      <Tooltip title={tr("Open puppy panel", "Abrir panel del cachorro")} arrow>
        <Box
          onClick={() => setOpenPanel(true)}
          sx={{
            position: "fixed",
            left: { xs: 10, sm: 25 },
            bottom: { xs: 54, sm: 62 },
            zIndex: 9999,
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <Box
            sx={{
              mb: 0.75,
              px: 1.2,
              py: 0.45,
              borderRadius: "999px",
              border: "1px solid rgba(255,152,0,0.8)",
              background: "rgba(0,0,0,0.72)",
              backdropFilter: "blur(8px)",
              color: "white",
              fontSize: 12,
              fontWeight: 800,
              textAlign: "center",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.7,
              boxShadow: "0 0 12px rgba(0,0,0,0.45)",
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: statusColor(pet.status),
                boxShadow: `0 0 10px ${statusColor(pet.status)}`,
              }}
            />
            {pet.status === "fallen"
              ? tr("I need help", "Necesito ayuda")
              : pet.status === "weak"
                ? tr("I feel weak", "Me siento débil")
                : animation === "eat"
                  ? tr("Eating", "Comiendo")
                  : animation === "drink"
                    ? tr("Drinking", "Bebiendo")
                    : animation === "play"
                      ? tr("Playing", "Jugando")
                      : animation === "walk"
                        ? tr("Walking", "Caminando")
                        : displayName}
          </Box>

          <Box
            sx={{
              width: { xs: 118, sm: 138 },
              height: { xs: 118, sm: 138 },
              borderRadius: "50%",
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "radial-gradient(circle at 30% 25%, rgba(255,255,255,0.24), rgba(255,255,255,0.08) 25%, rgba(0,170,255,0.08) 55%, rgba(255,152,0,0.16) 100%)",
              border: "2px solid rgba(255,255,255,0.24)",
              boxShadow:
                "0 0 25px rgba(0,160,255,0.22), 0 0 18px rgba(255,152,0,0.35), inset 0 0 20px rgba(255,255,255,0.08)",
              backdropFilter: "blur(6px)",
              overflow: "hidden",
              animation: pet.status === "fallen" ? "sphereStill 2s ease-in-out infinite" : "sphereFloat 4.2s ease-in-out infinite",
              "@keyframes sphereFloat": {
                "0%": { transform: "translateY(0px)" },
                "50%": { transform: "translateY(-10px)" },
                "100%": { transform: "translateY(0px)" },
              },
              "@keyframes sphereStill": {
                "0%": { transform: "translateY(0px)" },
                "50%": { transform: "translateY(-3px)" },
                "100%": { transform: "translateY(0px)" },
              },
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 14,
                left: 18,
                width: 24,
                height: 24,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.16)",
                filter: "blur(2px)",
              }}
            />

            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                animation:
                  animation === "idle"
                    ? "dogDrift 2.6s ease-in-out infinite"
                    : animation === "eat" || animation === "drink"
                      ? "dogActionNod 0.7s ease-in-out infinite"
                      : animation === "play"
                        ? "dogPlayHop 0.55s ease-in-out infinite"
                        : "dogDrift 2.6s ease-in-out infinite",
                "@keyframes dogDrift": {
                  "0%": { transform: "translateX(-6px) translateY(3px) rotate(-2deg)" },
                  "50%": { transform: "translateX(6px) translateY(-1px) rotate(2deg)" },
                  "100%": { transform: "translateX(-6px) translateY(3px) rotate(-2deg)" },
                },
                "@keyframes dogActionNod": {
                  "0%": { transform: "translateY(0px) scale(1)" },
                  "50%": { transform: "translateY(2px) scale(0.98)" },
                  "100%": { transform: "translateY(0px) scale(1)" },
                },
                "@keyframes dogPlayHop": {
                  "0%": { transform: "translateY(0px) scale(1)" },
                  "50%": { transform: "translateY(-7px) scale(1.03)" },
                  "100%": { transform: "translateY(0px) scale(1)" },
                },
              }}
            >
              <Box
                component="img"
                src={floatingImage}
                alt="Big Dog Bone Puppy"
                sx={{
                  width: { xs: 92, sm: 108 },
                  height: { xs: 92, sm: 108 },
                  objectFit: "contain",
                  filter: pet.status === "fallen" ? "grayscale(1) brightness(0.62)" : "drop-shadow(0 0 10px rgba(255,153,0,0.85))",
                }}
              />
            </Box>
          </Box>
        </Box>
      </Tooltip>

      {/* ======================================================
          MAIN PANEL
      ====================================================== */}
      <Modal open={openPanel} onClose={() => setOpenPanel(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "98vw", sm: "96vw", lg: "1180px", xl: "1280px" },
            height: { xs: "94vh", md: "88vh" },
            overflow: "hidden",
            background:
              "radial-gradient(circle at top, rgba(255,152,0,0.20), transparent 34%), linear-gradient(180deg, #151515, #050505)",
            border: "2px solid #ff9800",
            borderRadius: "24px",
            boxShadow: "0 0 45px rgba(255,152,0,0.50)",
            p: { xs: 1, sm: 1.4 },
            color: "white",
            display: "grid",
            gridTemplateRows: "auto auto 1fr auto",
            gap: 1,
          }}
        >
          {/* HEADER */}
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
            <Box>
              <Typography variant="h5" sx={{ color: "#ffb300", fontWeight: 950, lineHeight: 1.05 }}>
                🐶 {tr("Big Dog Bone Puppy", "Cachorro de Big Dog Bone")}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.72)", fontSize: 12 }}>
                {tr(
                  "Powered by CityFarm resources · One-screen care dashboard",
                  "Funciona con recursos de CityFarm · Panel de cuidado en una sola pantalla"
                )}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
              <Button
                size="small"
                variant={language === "en" ? "contained" : "outlined"}
                onClick={() => changeLanguage("en")}
                sx={{
                  color: language === "en" ? "#111" : "#fff",
                  backgroundColor: language === "en" ? "#fff" : "transparent",
                  borderColor: "rgba(255,255,255,0.65)",
                  fontWeight: 950,
                }}
              >
                English
              </Button>
              <Button
                size="small"
                variant={language === "es" ? "contained" : "outlined"}
                onClick={() => changeLanguage("es")}
                sx={{
                  color: language === "es" ? "#111" : "#fff",
                  backgroundColor: language === "es" ? "#fff" : "transparent",
                  borderColor: "rgba(255,255,255,0.65)",
                  fontWeight: 950,
                }}
              >
                Español
              </Button>

              <Chip
                label={statusText(pet.status, language)}
                sx={{ color: "black", fontWeight: 950, backgroundColor: statusColor(pet.status) }}
              />
              <Button size="small" variant="outlined" onClick={openCityFarm} sx={{ color: "#d5ffd7", borderColor: "#2e7d32", fontWeight: 950 }}>
                CityFarm
              </Button>
              <Button
                size="small"
                variant={showInstructions ? "contained" : "outlined"}
                onClick={() => {
                  setShowCaretakerHelp(false);
                  setShowInstructions((prev) => !prev);
                }}
                sx={{
                  color: showInstructions ? "black" : "#ffcc80",
                  backgroundColor: showInstructions ? "#ff9800" : "transparent",
                  borderColor: "#ff9800",
                  fontWeight: 950,
                }}
              >
                {tr("Instructions", "Instrucciones")}
              </Button>

              <Button
                size="small"
                variant={showCaretakerHelp ? "contained" : "outlined"}
                onClick={openCaretakerHelpPanel}
                sx={{
                  color: showCaretakerHelp ? "black" : "#90caf9",
                  backgroundColor: showCaretakerHelp ? "#90caf9" : "transparent",
                  borderColor: "#90caf9",
                  fontWeight: 950,
                }}
              >
                🐾 {tr("Caretaker Help", "Ayuda del Cuidador")}
              </Button>
            </Stack>
          </Box>

          {message && (
            <Alert severity={message.type} sx={{ py: 0.35 }}>
              {message.text}
            </Alert>
          )}

          {/* BODY */}
          <Box sx={{ minHeight: 0, overflow: "auto", pr: 0.25 }}>
            {showCaretakerHelp ? (
              <Box>
                <Alert severity="info" sx={{ mb: 1 }}>
                  {tr(
                    "The Dog Caretaker is an automatic care service. It keeps the puppy routine active every 5 hours while it has resources.",
                    "El Cuidador del Perro es un servicio de cuidado automático. Mantiene activa la rutina del cachorro cada 5 horas mientras haya recursos."
                  )}
                </Alert>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
                  <InfoCard title={`🐾 ${tr("Dog Caretaker Rules", "Reglas del Cuidador del Perro")}`}>
                    <SmallRow label={tr("Price", "Precio")} value={`${fmtNumber(caretakerPricePerDay)} HUESO ${tr("per day", "por día")}`} accent />
                    <SmallRow label={tr("Maximum purchase", "Compra máxima")} value={`${caretakerMaxDays || 20} ${tr("days", "días")}`} />
                    <SmallRow label={tr("Automatic care", "Cuidado automático")} value={`${tr("Every", "Cada")} ${caretakerRunEveryHours}h: ${tr("Feed + Water + Play + Walk", "Alimentar + Agua + Jugar + Caminar")}`} />
                    <SmallRow label={tr("Payment", "Pago")} value={tr("Uses HUESO from Big Dog Bone", "Usa HUESO de Big Dog Bone")} />
                    <SmallRow label={tr("Event logs", "Registros de eventos")} value="pet_feed, pet_water, pet_play, pet_walk" />
                  </InfoCard>

                  <InfoCard title={`⚠️ ${tr("Automatic Disable Rule", "Regla de desactivación automática")}`}>
                    <Typography sx={{ color: "rgba(255,255,255,0.78)", fontSize: 13, mb: 1 }}>
                      {tr(
                        "If the caretaker cannot find enough Croquettes or Water in CityFarm, the service turns off automatically and saves the exact reason. To activate it again, the player must buy new caretaker days.",
                        "Si el cuidador no encuentra suficientes Croquetas o Agua en CityFarm, el servicio se desactiva automáticamente y guarda el motivo exacto. Para activarlo de nuevo, el jugador debe comprar nuevos días de cuidador."
                      )}
                    </Typography>
                    <SmallRow label={tr("Needs Croquettes", "Necesita Croquetas")} value={tr("Yes", "Sí")} accent />
                    <SmallRow label={tr("Needs Water", "Necesita Agua")} value={tr("Yes", "Sí")} accent />
                    <SmallRow label={tr("No resources", "Sin recursos")} value={tr("Caretaker disabled + reason saved", "Cuidador desactivado + motivo guardado")} />
                    <SmallRow label={tr("Reactivation", "Reactivación")} value={tr("Buy again", "Comprar de nuevo")} />
                  </InfoCard>
                </Box>

                <InfoCard title={tr("Current Caretaker Status", "Estado actual del cuidador")} sx={{ mt: 1 }}>
                  <SmallRow label={tr("Status", "Estado")} value={caretakerActive ? tr("✅ Active", "✅ Activo") : tr("❌ Inactive", "❌ Inactivo")} accent={caretakerActive} />
                  <SmallRow label={tr("Days remaining", "Días restantes")} value={fmtNumber(caretakerDaysRemaining, 0)} accent={caretakerActive} />
                  <SmallRow label={tr("Last care", "Último cuidado")} value={fmtDateTime(caretakerLastRunAt)} />
                  <SmallRow label={tr("Next care", "Próximo cuidado")} value={fmtDateTime(caretakerNextRunAt)} accent={caretakerActive} />
                  <SmallRow label={tr("Runs every", "Se ejecuta cada")} value={`${caretakerRunEveryHours} ${tr("hours", "horas")}`} accent />
                  <SmallRow label={tr("Total runs", "Ejecuciones totales")} value={fmtNumber(caretakerTotalRuns, 0)} />
                  <SmallRow label={tr("Ends at", "Finaliza el")} value={fmtDateTime(caretaker?.endsAt || rawPet?.caretakerEndsAt || caretaker?.caretakerEndsAt)} />
                  <SmallRow label={tr("Disabled reason", "Motivo de desactivación")} value={caretakerDisabledText} />
                  {caretakerLastRunMessage && <SmallRow label={tr("Last trace", "Último registro")} value={caretakerLastRunMessage} accent />}
                </InfoCard>

                <InfoCard title={tr("Caretaker Activity Trace", "Registro de actividad del cuidador")} sx={{ mt: 1 }}>
                  {caretakerHistory.length ? (
                    caretakerHistory.map((row, index) => (
                      <Box key={`${row?.at || index}-${index}`} sx={{ mb: index === caretakerHistory.length - 1 ? 0 : 0.75 }}>
                        <SmallRow label={fmtDateTime(row?.at)} value={row?.status || row?.type || tr("Trace", "Registro")} accent={String(row?.status || "").toUpperCase() === "OK"} />
                        <Typography sx={{ color: "rgba(255,255,255,0.68)", fontSize: 12 }}>
                          {row?.message || caretakerReasonText(row?.reason, language) || tr("Trace saved.", "Registro guardado.")}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography sx={{ color: "rgba(255,255,255,0.64)", fontSize: 12 }}>
                      {tr(
                        "No caretaker trace yet. After buying or after each automatic run, the activity will appear here.",
                        "Todavía no hay registros del cuidador. Después de comprar o de cada ejecución automática, la actividad aparecerá aquí."
                      )}
                    </Typography>
                  )}
                </InfoCard>

                <InfoCard title={tr("Buy Caretaker Days", "Comprar días de cuidador")} sx={{ mt: 1 }}>
                  <TextField
                    size="small"
                    type="number"
                    value={caretakerDays}
                    onChange={(e) => setCaretakerDays(clampCaretakerDays(e.target.value))}
                    inputProps={{ min: 1, max: 20 }}
                    sx={{
                      width: 140,
                      mr: 1,
                      input: { color: "white", fontWeight: 900 },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#90caf9" },
                        "&:hover fieldset": { borderColor: "#bbdefb" },
                        "&.Mui-focused fieldset": { borderColor: "#e3f2fd" },
                      },
                    }}
                  />
                  <Chip
                    label={`${tr("Total", "Total")}: ${fmtNumber(caretakerTotal)} HUESO`}
                    sx={{ color: "#001d35", fontWeight: 950, backgroundColor: "#90caf9", mr: 1 }}
                  />
                  <Button disabled={loading} variant="contained" onClick={buyCaretaker} sx={{ mt: { xs: 1, sm: 0 }, backgroundColor: "#1976d2", fontWeight: 950 }}>
                    {tr("Buy / Activate", "Comprar / Activar")}
                  </Button>
                  <Button disabled={loading || !caretakerActive} variant="outlined" onClick={cancelCaretaker} sx={{ mt: { xs: 1, sm: 0 }, ml: { xs: 0, sm: 1 }, color: "#ffcc80", borderColor: "#ff9800", fontWeight: 950 }}>
                    {tr("Cancel", "Cancelar")}
                  </Button>
                </InfoCard>

                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowCaretakerHelp(false);
                    setShowInstructions(false);
                  }}
                  sx={{ mt: 1, color: "#ffcc80", borderColor: "#ff9800", fontWeight: 950 }}
                >
                  {tr("Back to Puppy Panel", "Volver al panel del cachorro")}
                </Button>
              </Box>
            ) : !showInstructions ? (
              <Box
                sx={{
                  minHeight: 0,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "275px 1fr 330px" },
                  gap: 1,
                }}
              >
                {/* LEFT */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
                  <InfoCard title={tr("Puppy", "Cachorro")} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Box
                      sx={{
                        width: { xs: 175, md: 205 },
                        height: { xs: 175, md: 205 },
                        borderRadius: "50%",
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background:
                          "radial-gradient(circle at 30% 25%, rgba(255,255,255,0.24), rgba(255,255,255,0.08) 25%, rgba(0,170,255,0.08) 55%, rgba(255,152,0,0.16) 100%)",
                        border: "2px solid rgba(255,255,255,0.24)",
                        boxShadow:
                          "0 0 25px rgba(0,160,255,0.22), 0 0 18px rgba(255,152,0,0.35), inset 0 0 20px rgba(255,255,255,0.08)",
                        mb: 1,
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        component="img"
                        src={panelImage}
                        alt="Pet"
                        sx={{
                          width: { xs: 145, md: 168 },
                          height: { xs: 145, md: 168 },
                          objectFit: "contain",
                          filter: pet.status === "fallen" ? "grayscale(1) brightness(0.62)" : "drop-shadow(0 0 16px rgba(255,153,0,0.85))",
                        }}
                      />
                    </Box>

                    <Typography sx={{ fontWeight: 950, color: "#ffcc80", fontSize: 16 }}>{displayName}</Typography>
                    <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: 12 }}>{stageText(pet.stage, language)}</Typography>
                  </InfoCard>

                  <InfoCard title={tr("Quick Info", "Información rápida")}>
                    <SmallRow label={tr("Birth name", "Nombre de nacimiento")} value={pet.birthName} />
                    <SmallRow label={tr("Level", "Nivel")} value={pet.level} accent />
                    <SmallRow label="XP" value={pet.xp} />
                    <SmallRow label={tr("Age", "Edad")} value={`${pet.ageDays || 0} ${tr("days", "días")}`} />
                    <SmallRow label={tr("Status", "Estado")} value={statusText(pet.status, language)} accent />
                  </InfoCard>

                  <InfoCard title={tr("Nickname", "Apodo")}>
                    <TextField
                      size="small"
                      fullWidth
                      value={nickname}
                      onChange={(e) => setNickname(e.target.value)}
                      placeholder={tr("Puppy nickname", "Apodo del cachorro")}
                      sx={{
                        input: { color: "white" },
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": { borderColor: "#ff9800" },
                          "&:hover fieldset": { borderColor: "#ffb300" },
                          "&.Mui-focused fieldset": { borderColor: "#ffcc80" },
                        },
                      }}
                    />
                    <Button
                      fullWidth
                      disabled={loading}
                      variant="outlined"
                      onClick={saveNickname}
                      sx={{ mt: 0.8, color: "#ffb300", borderColor: "#ffb300", fontWeight: 950 }}
                    >
                      {tr("Save Nickname", "Guardar apodo")}
                    </Button>
                  </InfoCard>
                </Box>

                {/* CENTER */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
                  <InfoCard title={tr("Puppy Status", "Estado del cachorro")}>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: { xs: 0, sm: 1.2 } }}>
                      <StatBar label={tr("Life", "Vida")} icon="❤️" value={pet.life?.current || 0} />
                      <StatBar label={tr("Health", "Salud")} icon="🩺" value={pet.health} />
                      <StatBar label={tr("Hunger", "Hambre")} icon="🍖" value={pet.hunger} />
                      <StatBar label={tr("Thirst", "Sed")} icon="💧" value={pet.thirst} />
                      <StatBar label={tr("Happiness", "Felicidad")} icon="😊" value={pet.happiness} />
                      <StatBar label={tr("Energy", "Energía")} icon="⚡" value={pet.energy} />
                    </Box>
                  </InfoCard>

                  <InfoCard title={tr("Smart Actions", "Acciones inteligentes")}>
                    <Alert
                      severity={priorityHint.includes("Critical") || priorityHint.includes("Crítico") ? "error" : priorityHint.includes("Attention") || priorityHint.includes("Atención") ? "warning" : "success"}
                      sx={{ mb: 1, py: 0.35 }}
                    >
                      {priorityHint}
                    </Alert>

                    {canUseActions ? (
                      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}>
                        <ActionButton
                          disabled={loading}
                          urgency={hungerUrgency}
                          onClick={feedPet}
                          sx={{ backgroundColor: "#ff9800" }}
                        >
                          🍖 {tr("Feed", "Alimentar")}
                        </ActionButton>

                        <ActionButton
                          disabled={loading}
                          urgency={thirstUrgency}
                          onClick={waterPet}
                          sx={{ backgroundColor: "#2196f3" }}
                        >
                          💧 {tr("Give Water", "Dar Agua")}
                        </ActionButton>

                        <ActionButton
                          disabled={loading}
                          onClick={walkPet}
                          sx={{ backgroundColor: "#795548" }}
                        >
                          🐕 {tr("Walk", "Caminar")}
                        </ActionButton>

                        <ActionButton
                          disabled={loading}
                          urgency={happinessUrgency}
                          onClick={playPet}
                          sx={{ backgroundColor: "#4caf50" }}
                        >
                          🎾 {tr("Play", "Jugar")}
                        </ActionButton>

                        <ActionButton
                          disabled={loading}
                          readyPulseOn={claimReady}
                          onClick={claimDaily}
                          sx={{ backgroundColor: "#9c27b0", gridColumn: { xs: "1", sm: "1 / span 2" } }}
                        >
                          🦴 {tr("Claim Daily", "Reclamar recompensa diaria")}
                        </ActionButton>
                      </Box>
                    ) : (
                      <Box sx={{ display: "grid", gap: 1 }}>
                        <Alert severity="error">{tr("Your puppy is fallen. Revive it to recover benefits by paying 1000 Mini-Hueso or Hueso.", "Tu cachorro está caído. Revívelo para recuperar los beneficios pagando 1000 Mini-Hueso o HUESO.")}</Alert>
                        <ActionButton disabled={loading} onClick={() => revivePet("dinero")} sx={{ backgroundColor: "#ff9800" }}>
                          {tr("Revive with 1000 Mini-Hueso", "Revivir con 1000 Mini-Hueso")}
                        </ActionButton>
                        <ActionButton disabled={loading} onClick={() => revivePet("dineroReal")} sx={{ backgroundColor: "#f44336" }}>
                          {tr("Revive with 1000 HUESO (In-Game)", "Revivir con 1000 HUESO del juego")}
                        </ActionButton>
                      </Box>
                    )}
                  </InfoCard>

                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}>
                    <InfoCard title={tr("Cooldowns", "Tiempos de espera")}>
                      <SmallRow label={tr("Feed", "Alimentar")} value={cooldowns?.feed?.text || tr("Ready", "Listo")} />
                      <SmallRow label={tr("Water", "Agua")} value={cooldowns?.water?.text || tr("Ready", "Listo")} />
                      <SmallRow label={tr("Play", "Jugar")} value={cooldowns?.play?.text || tr("Ready", "Listo")} />
                      <SmallRow label={tr("Walk", "Caminar")} value={cooldowns?.walk?.text || tr("Ready", "Listo")} />
                      <SmallRow label={tr("Claim", "Reclamar")} value={cooldowns?.claim?.text || tr("Ready", "Listo")} accent={claimReady} />
                    </InfoCard>

                    <InfoCard title={tr("Current Level Benefit", "Beneficio del nivel actual")}>
                      <SmallRow label={`${tr("Level", "Nivel")} ${currentLevelBenefit.level}`} value={currentLevelBenefit.benefit} accent />
                      <Typography sx={{ color: "rgba(255,255,255,0.62)", fontSize: 12 }}>{currentLevelBenefit.daily}</Typography>
                    </InfoCard>
                  </Box>
                </Box>

                {/* RIGHT */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
                  <InfoCard title={tr("Big Dog Bone Balances", "Saldos de Big Dog Bone")}>
                    <SmallRow label="Mini- Hueso" value={fmtNumber(bdb?.dinero || 0)} accent />
                    <SmallRow label={tr("Hueso (In Game)", "HUESO del juego")} value={fmtNumber(bdb?.dineroReal || 0)} />
                    <SmallRow
                      label={tr("Energy", "Energía")}
                      value={
                        bdb?.energia?.actual !== undefined
                          ? `${fmtNumber(bdb?.energia?.actual)} / ${fmtNumber(bdb?.energia?.maximo)}`
                          : "—"
                      }
                      accent
                    />
                  </InfoCard>

                  <InfoCard title={tr("CityFarm Resources", "Recursos de CityFarm")}>
                    <SmallRow label={`💧 ${tr("Water", "Agua")}`} value={fmtNumber(waterQty)} accent={thirstUrgency !== "normal"} />
                    <SmallRow label={`🍖 ${tr("Croquettes", "Croquetas")}`} value={fmtNumber(foodQty)} accent={hungerUrgency !== "normal"} />
                    <Button fullWidth variant="contained" onClick={openCityFarm} sx={{ mt: 0.8, backgroundColor: "#2e7d32", fontWeight: 950 }}>
                      {tr("Produce More in CityFarm", "Producir más en CityFarm")}
                    </Button>
                  </InfoCard>

                  <InfoCard title={`🐾 ${tr("Dog Caretaker", "Cuidador del Perro")}`}>
                    <SmallRow label={tr("Status", "Estado")} value={caretakerActive ? tr("✅ Active", "✅ Activo") : tr("❌ Inactive", "❌ Inactivo")} accent={caretakerActive} />
                    <SmallRow label={tr("Days left", "Días restantes")} value={fmtNumber(caretakerDaysRemaining, 0)} />
                    <SmallRow label={tr("Runs every", "Se ejecuta cada")} value={`${caretakerRunEveryHours} ${tr("hours", "horas")}`} accent />
                    <SmallRow label={tr("Next care", "Próximo cuidado")} value={fmtDateTime(caretakerNextRunAt)} accent={caretakerActive} />
                    <SmallRow label={tr("Last care", "Último cuidado")} value={fmtDateTime(caretakerLastRunAt)} />
                    <SmallRow label={tr("Total runs", "Ejecuciones totales")} value={fmtNumber(caretakerTotalRuns, 0)} />
                    {!caretakerActive && caretakerDisabledReason && <SmallRow label={tr("Why inactive", "Por qué está inactivo")} value={caretakerDisabledText} accent />}
                    {caretakerLastRunMessage && <SmallRow label={tr("Trace", "Registro")} value={caretakerLastRunMessage} accent={caretakerActive} />}
                    <SmallRow label={tr("Price", "Precio")} value={`${fmtNumber(caretakerPricePerDay)} HUESO/${tr("day", "día")}`} accent />
                    <SmallRow label={tr("Total selected", "Total seleccionado")} value={`${fmtNumber(caretakerTotal)} HUESO`} />
                    <Box sx={{ display: "grid", gridTemplateColumns: "76px 1fr", gap: 0.7, mt: 0.8 }}>
                      <TextField
                        size="small"
                        type="number"
                        value={caretakerDays}
                        onChange={(e) => setCaretakerDays(clampCaretakerDays(e.target.value))}
                        inputProps={{ min: 1, max: 20 }}
                        sx={{
                          input: { color: "white", fontWeight: 900, textAlign: "center" },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "#90caf9" },
                            "&:hover fieldset": { borderColor: "#bbdefb" },
                            "&.Mui-focused fieldset": { borderColor: "#e3f2fd" },
                          },
                        }}
                      />
                      <Button disabled={loading} variant="contained" onClick={buyCaretaker} sx={{ backgroundColor: "#1976d2", fontWeight: 950 }}>
                        {tr("Buy Care", "Comprar cuidado")}
                      </Button>
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.7, mt: 0.7 }}>
                      <Button variant="outlined" onClick={openCaretakerHelpPanel} sx={{ color: "#90caf9", borderColor: "#90caf9", fontWeight: 950 }}>
                        {tr("Help / Rules", "Ayuda / Reglas")}
                      </Button>
                      <Button disabled={loading || !caretakerActive} variant="outlined" onClick={cancelCaretaker} sx={{ color: "#ffcc80", borderColor: "#ff9800", fontWeight: 950 }}>
                        {tr("Cancel", "Cancelar")}
                      </Button>
                    </Box>
                    <Box sx={{ mt: 0.9, p: 0.8, borderRadius: 2, background: "rgba(255,255,255,0.045)", border: "1px solid rgba(144,202,249,0.18)" }}>
                      <Typography sx={{ color: "#90caf9", fontSize: 12, fontWeight: 950, mb: 0.35 }}>
                        {tr("Recent caretaker trace", "Registro reciente del cuidador")}
                      </Typography>
                      {caretakerHistory.length ? (
                        caretakerHistory.slice(0, 3).map((row, index) => (
                          <Typography key={`${row?.at || index}-${index}`} sx={{ color: "rgba(255,255,255,0.70)", fontSize: 11.5, mb: 0.25 }}>
                            • {fmtDateTime(row?.at)} — {row?.message || caretakerReasonText(row?.reason, language) || row?.status || row?.type}
                          </Typography>
                        ))
                      ) : (
                        <Typography sx={{ color: "rgba(255,255,255,0.58)", fontSize: 11.5 }}>
                          {tr("No trace yet.", "Todavía no hay registros.")}
                        </Typography>
                      )}
                    </Box>
                  </InfoCard>

                  <InfoCard title={tr("Current Benefit", "Beneficio actual")}>
                    <SmallRow label={tr("Status", "Estado")} value={benefit?.active ? tr("✅ Active", "✅ Activo") : tr("❌ Inactive", "❌ Inactivo")} accent={benefit?.active} />
                    <SmallRow label={tr("Daily claim", "Recompensa diaria")} value={`${fmtNumber(claimAmount)} Mini-Hueso`} />
                    <SmallRow label={tr("Mini-Hueso bonus", "Bono de Mini-Hueso")} value={`${benefit?.bdb?.miniHuesoBonusPercent || 0}%`} />
                    <SmallRow label={tr("Energy bonus", "Bono de energía")} value={`${benefit?.bdb?.energyBonusPercent || 0}%`} />
                  </InfoCard>

                    <InfoCard title={tr("Neglect Warning", "Advertencia por descuido")}>
                    <Typography sx={{ color: "rgba(255,255,255,0.78)", fontSize: 13 }}>
                      {tr(
                        "If you neglect your puppy, its stats can drop over time. It consumes Water and Croquettes every 5 hours when the caretaker runs, according to backend config. Feed, water, and play buttons now blink according to urgency so the player knows what to do first.",
                        "Si descuidas a tu cachorro, sus estadísticas pueden bajar con el tiempo. Consume Agua y Croquetas cada 5 horas cuando se ejecuta el cuidador, según la configuración del backend. Los botones de alimentar, dar agua y jugar ahora parpadean según la urgencia para indicar qué debe hacerse primero."
                      )}
                    </Typography>
                  </InfoCard>
                </Box>
              </Box>
            ) : (
              <Box>
                <Alert severity="info" sx={{ mb: 1 }}>
                  {tr(
                    "Your puppy lives in Big Dog Bone, but it uses Water and Croquettes produced in CityFarm.",
                    "Tu cachorro vive en Big Dog Bone, pero utiliza Agua y Croquetas producidas en CityFarm."
                  )}
                </Alert>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
                  <InfoCard title={tr("Daily Care", "Cuidado diario")}>
                    <SmallRow label={tr("Feed", "Alimentar")} value={tr("Uses 2 Croquettes from CityFarm", "Usa 2 Croquetas de CityFarm")} />
                    <SmallRow label={tr("Water", "Agua")} value={tr("Uses 2 Water from CityFarm", "Usa 2 de Agua de CityFarm")} />
                    <SmallRow label={tr("Play", "Jugar")} value={tr("Improves happiness", "Mejora la felicidad")} />
                    <SmallRow label={tr("Walk", "Caminar")} value={tr("Improves happiness, health and XP", "Mejora la felicidad, la salud y la XP")} />
                    <SmallRow label={tr("Daily claim", "Recompensa diaria")} value={tr("Gives Mini-Hueso when conditions are met", "Entrega Mini-Hueso cuando se cumplen las condiciones")} />
                  </InfoCard>

                  <InfoCard title={tr("How to Keep Benefits Active", "Cómo mantener activos los beneficios")}>
                    <SmallRow label={tr("Hunger", "Hambre")} value={tr("Keep above 55%", "Mantener por encima de 55%")} />
                    <SmallRow label={tr("Thirst", "Sed")} value={tr("Keep above 55%", "Mantener por encima de 55%")} />
                    <SmallRow label={tr("Happiness", "Felicidad")} value={tr("Keep above 45%", "Mantener por encima de 45%")} />
                    <SmallRow label={tr("Health", "Salud")} value={tr("Keep above 65%", "Mantener por encima de 65%")} />
                    <SmallRow label={tr("Life", "Vida")} value={tr("Keep above 25%", "Mantener por encima de 25%")} />
                  </InfoCard>
                </Box>

                <InfoCard title={tr("Neglect Rule", "Regla de descuido")} sx={{ mt: 1 }}>
                  <Typography sx={{ color: "rgba(255,255,255,0.76)", fontSize: 13 }}>
                    {tr(
                      "If the puppy is forgotten, hunger, thirst, happiness, health, and life go down over time. If it reaches a critical state, it can become weak or fallen. XP or level loss must be enforced by the backend for full security.",
                      "Si el cachorro es olvidado, el hambre, la sed, la felicidad, la salud y la vida disminuyen con el tiempo. Si llega a un estado crítico, puede quedar débil o caído. La pérdida de XP o nivel debe ser aplicada por el backend para mantener la seguridad."
                    )}
                  </Typography>
                </InfoCard>

                <InfoCard title={tr("Benefits by Level", "Beneficios por nivel")} sx={{ mt: 1 }}>
                  {benefitLevels.map((row, index) => (
                    <Box key={row.level}>
                      <SmallRow label={`${tr("Level", "Nivel")} ${row.level}`} value={localizeBenefitLevel(row, language).benefit} />
                      <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12, mb: 0.6 }}>{localizeBenefitLevel(row, language).daily}</Typography>
                      {index !== benefitLevels.length - 1 && <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mb: 0.6 }} />}
                    </Box>
                  ))}
                </InfoCard>

                <InfoCard title={tr("Dog Caretaker", "Cuidador del Perro")} sx={{ mt: 1 }}>
                  <Typography sx={{ color: "rgba(255,255,255,0.76)", fontSize: 13, mb: 0.8 }}>
                    {tr(
                      "The caretaker costs 500 HUESO per day and can be bought for up to 20 days. It performs automatic care every 5 hours, leaves visible traces, and disables itself with a clear reason if CityFarm does not have enough Water or Croquettes.",
                      "El cuidador cuesta 500 HUESO por día y puede comprarse hasta por 20 días. Realiza cuidados automáticos cada 5 horas, deja registros visibles y se desactiva indicando claramente el motivo cuando CityFarm no tiene suficiente Agua o Croquetas."
                    )}
                  </Typography>
                  <Button variant="outlined" onClick={openCaretakerHelpPanel} sx={{ color: "#90caf9", borderColor: "#90caf9", fontWeight: 950 }}>
                    {tr("Open Caretaker Help", "Abrir ayuda del cuidador")}
                  </Button>
                </InfoCard>

                <Button variant="contained" onClick={openCityFarm} sx={{ mt: 1, backgroundColor: "#2e7d32", fontWeight: 950 }}>
                  {tr(
                    "Open CityFarm to produce Water and Croquettes by visiting other farms and helping them harvest and plant their lands and factories.",
                    "Abre CityFarm para producir Agua y Croquetas visitando otras granjas y ayudándolas a cosechar y sembrar sus terrenos y fábricas."
                  )}
                </Button>
              </Box>
            )}
          </Box>

          {/* FOOTER */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12 }}>
              {lastUpdated ? `${tr("Updated", "Actualizado")}: ${lastUpdated.toLocaleTimeString()}` : tr("Not updated yet", "Todavía no se ha actualizado")}
            </Typography>

            <Button
              disabled={loading}
              variant="text"
              onClick={() => refreshPet({ silent: false, keepMessage: false, syncGlobal: true })}
              sx={{ color: "#ffcc80", fontWeight: 950 }}
            >
              {loading ? tr("Updating...", "Actualizando...") : tr("Refresh All", "Actualizar todo")}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
